/**
 * Timeline rail for the intent flow animation (Requirements 6.1, 6.2, 6.3, 6.4).
 *
 * The rail is the clickable interaction-timeline shown alongside the flow graph — modelled on the
 * AgentDNA dashboard's "Interaction Timeline". It renders a scrollable, numbered list of the flow's
 * timed `FlowStep`s. Each row is a real `<button>` so the list is keyboard-operable and the active
 * round's rows are marked with `aria-current` (6.4).
 *
 * - 6.1 — Each row shows its from→to endpoints (`nodeLabel(step.from)` → `nodeLabel(step.to)`), its
 *   latency rendered as elapsed time (`formatLatency`), and a verdict colour (a `--blocked` modifier
 *   reusing the platform's threat/error convention for blocked hops).
 * - 6.2 — When a round is active, the active rows are highlighted and the first active row is
 *   scrolled into view — EXCEPT while the operator is manually scrolling the rail, when the
 *   auto-scroll is suppressed so a programmatic scroll never fights the operator's current view. The
 *   "manually scrolling" state is a short-lived ref flag set by an `onScroll` handler and cleared on
 *   a timeout; the gating is factored into `shouldAutoScroll` + `scrollRowIntoView` so task 11.4's
 *   example test can assert it directly.
 * - 6.3 — Clicking a row calls `onSelectStep(stepIndex)`; the page wires that to `playback.jump` for
 *   the round that contains the step.
 * - 6.4 — The rows are real labelled `<button>`s (an `aria-label` describing the hop), focusable and
 *   tab-reachable, in a `<ul>`/`<li>` list.
 *
 * All DOM calls are guarded (`el?.scrollIntoView?.(...)`) so JSDOM tests do not throw, and the
 * reduced-motion flag selects an instant (`behavior: "auto"`) scroll rather than a smooth one.
 */

import { useEffect, useRef, type ReactNode } from "react";
import type { FlowStep } from "./flowData.ts";

/** How long after the last manual scroll the rail keeps suppressing auto-scroll (ms). */
export const MANUAL_SCROLL_IDLE_MS = 600;

export interface TimelineRailProps {
  /** The flow's timed hops, in order. Row N corresponds to `steps[N]`. */
  readonly steps: readonly FlowStep[];
  /** Resolve a node id to a display name (the page passes a lookup over the flow's nodes). */
  readonly nodeLabel: (id: string) => string;
  /** Step indices lit by the active round (from the playback hook). */
  readonly activeSteps: readonly number[];
  /** Called with the clicked row's step index; the page jumps playback to that step's round. */
  readonly onSelectStep: (stepIndex: number) => void;
  /** When set, scroll instantly (never smooth) and never animate. Defaults to the OS preference. */
  readonly reducedMotion?: boolean;
}

/**
 * Render a latency (in ms) as human-readable elapsed time: sub-second values as `N ms`, one second
 * and above as `X.Y s` (one decimal, trailing `.0` trimmed). Negative/non-finite values clamp to
 * `0 ms` so a row never shows garbage.
 */
export function formatLatency(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0 ms";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  // One decimal place, but render whole seconds without a trailing ".0".
  const rounded = Math.round(seconds * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded} s` : `${rounded.toFixed(1)} s`;
}

/** Two-digit, 1-based row number like the AgentDNA rail (01, 02, …, 10, 11). */
export function rowNumber(index: number): string {
  return String(index + 1).padStart(2, "0");
}

/**
 * The auto-scroll gate (6.2): the rail auto-scrolls to the active step only when there IS an active
 * step and the operator is NOT currently scrolling the rail by hand. Pure and side-effect free so
 * the example test can assert the gate without a DOM.
 */
export function shouldAutoScroll(activeSteps: readonly number[], userScrolling: boolean): boolean {
  return activeSteps.length > 0 && !userScrolling;
}

/**
 * Scroll a row element into view, guarded for JSDOM (where `scrollIntoView` may be absent) and
 * respecting reduced motion by using an instant scroll. Centralised so the effect and the test share
 * one code path.
 */
export function scrollRowIntoView(el: HTMLElement | null | undefined, reducedMotion: boolean): void {
  el?.scrollIntoView?.({ behavior: reducedMotion ? "auto" : "smooth", block: "nearest" });
}

export function TimelineRail(props: TimelineRailProps): ReactNode {
  const { steps, nodeLabel, activeSteps, onSelectStep, reducedMotion = false } = props;

  // Short-lived "operator is scrolling the rail by hand" flag. A ref (not state) so toggling it never
  // re-renders and never fights the programmatic scroll it guards.
  const userScrollingRef = useRef(false);
  const idleTimerRef = useRef<number | null>(null);
  // One ref per row so the active row can be scrolled into view.
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Clear any pending idle timer on unmount so a timeout never fires after teardown.
  useEffect(() => {
    return () => {
      if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
    };
  }, []);

  // Auto-scroll the active row into view when the active round changes — unless the operator is
  // manually scrolling the rail (6.2). The gate is checked here, before any DOM call.
  const firstActive = activeSteps.length > 0 ? (activeSteps[0] ?? -1) : -1;
  useEffect(() => {
    if (!shouldAutoScroll(activeSteps, userScrollingRef.current)) return;
    scrollRowIntoView(rowRefs.current[firstActive], reducedMotion);
    // Depend on `firstActive` (and the active set) so a new active round re-triggers the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstActive, activeSteps, reducedMotion]);

  // Mark the rail as "manually scrolling" for a short window after each scroll event, so the next
  // active-round change does not yank the operator's view (6.2). A programmatic scroll also fires
  // `onScroll`, but by then the effect above has already run under a false flag, so it is harmless.
  const handleScroll = (): void => {
    userScrollingRef.current = true;
    if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => {
      userScrollingRef.current = false;
      idleTimerRef.current = null;
    }, MANUAL_SCROLL_IDLE_MS);
  };

  const activeSet = new Set(activeSteps);

  return (
    <ul className="adna-rail" aria-label="Interaction timeline" onScroll={handleScroll}>
      {steps.map((step, i) => {
        const from = nodeLabel(step.from);
        const to = nodeLabel(step.to);
        const blocked = step.verdict === "blocked";
        const active = activeSet.has(i);
        const latency = formatLatency(step.latency);
        const rowClass = [
          "adna-rail__row",
          active ? "adna-rail__row--active" : "",
          blocked ? "adna-rail__row--blocked" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <li className="adna-rail__item" key={`${step.spanId}-${i}`}>
            <button
              type="button"
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              className={rowClass}
              aria-current={active ? "step" : undefined}
              aria-label={`Step ${i + 1}: ${from} to ${to}, ${latency}, ${blocked ? "blocked" : "allowed"}`}
              onClick={() => onSelectStep(i)}
            >
              <span className="adna-rail__num" aria-hidden="true">
                {rowNumber(i)}
              </span>
              <span className="adna-rail__hop">
                <span className="adna-rail__endpoint">{from}</span>
                <span className="adna-rail__arrow" aria-hidden="true">
                  →
                </span>
                <span className="adna-rail__endpoint">{to}</span>
              </span>
              <span className="adna-rail__latency">{latency}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
